import { useState } from 'react';
import { FaCamera, FaTimes, FaCloudUploadAlt } from 'react-icons/fa';
import { buscarDirecciones, enviarPresupuesto } from '../services/api';
import styles from '../styles/pages.module.css';

const MAX_FOTOS = 8;
const MAX_SIZE_MB = 8;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'image/gif'];

function AddressAutocomplete({ label, name, value, selected, onValueChange, onSelect, placeholder }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const handleChange = (e) => {
    const next = e.target.value;
    onValueChange(name, next);
    setOpen(true);

    window.clearTimeout(AddressAutocomplete.timer?.[name]);
    AddressAutocomplete.timer = AddressAutocomplete.timer || {};
    AddressAutocomplete.timer[name] = window.setTimeout(async () => {
      if (next.trim().length < 3) {
        setSuggestions([]);
        return;
      }
      setLoading(true);
      try {
        const res = await buscarDirecciones(next.trim());
        setSuggestions(res.data?.suggestions || []);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 350);
  };

  return (
    <div className={styles.formGroup}>
      <label>{label}</label>
      <div className={styles.addressAutocomplete}>
        <input
          type="text"
          name={name}
          value={value}
          onChange={handleChange}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          autoComplete="off"
        />
        {selected && <div className={styles.addressValid}>Dirección validada</div>}
        {open && (loading || suggestions.length > 0) && (
          <div className={styles.addressSuggestions}>
            {loading && <div className={styles.addressSuggestionMuted}>Buscando direcciones...</div>}
            {!loading && suggestions.map(s => (
              <button
                type="button"
                key={s.id}
                className={styles.addressSuggestion}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onSelect(name, s);
                  setSuggestions([]);
                  setOpen(false);
                }}
              >
                <strong>{s.label.split(',').slice(0, 2).join(',')}</strong>
                <span>{s.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Presupuesto() {
  const [paso, setPaso] = useState(1);
  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [error, setError] = useState('');
  const [fotos, setFotos] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState({ origen: null, destino: null });
  const [form, setForm] = useState({
    tipoMudanza: 'local',
    fechaMudanza: '',
    origen: '',
    destino: '',
    descripcion: '',
    nombre: '',
    apellidos: '',
    email: '',
    telefono: '',
    aceptaLOPD: false,
  });

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setForm(f => ({
      ...f,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddressValue = (name, value) => {
    setForm(f => ({ ...f, [name]: value }));
    setSelectedAddress(a => ({ ...a, [name]: null }));
  };

  const handleAddressSelect = (name, suggestion) => {
    setForm(f => ({ ...f, [name]: suggestion.label }));
    setSelectedAddress(a => ({ ...a, [name]: suggestion }));
  };

  const handleFotos = (e) => {
    setError('');
    const nuevos = Array.from(e.target.files || []);
    const validos = [];
    const rechazados = [];
    for (const f of nuevos) {
      if (!ALLOWED_TYPES.includes(f.type)) {
        rechazados.push(`${f.name}: tipo no válido`);
        continue;
      }
      if (f.size > MAX_SIZE_MB * 1024 * 1024) {
        rechazados.push(`${f.name}: supera ${MAX_SIZE_MB}MB`);
        continue;
      }
      validos.push(f);
    }
    const total = fotos.length + validos.length;
    if (total > MAX_FOTOS) {
      setError(`Máximo ${MAX_FOTOS} fotos. Tienes ${fotos.length} ya seleccionadas.`);
      return;
    }
    if (rechazados.length) setError(rechazados.join(' · '));
    setFotos([...fotos, ...validos]);
    e.target.value = '';
  };

  const quitarFoto = (i) => {
    setFotos(fotos.filter((_, idx) => idx !== i));
  };

  const siguientePaso = () => {
    setError('');
    if (paso === 1 && !form.tipoMudanza) {
      setError('Por favor selecciona un tipo de mudanza');
      return;
    }
    if (paso === 2 && (!selectedAddress.origen || !selectedAddress.destino)) {
      setError('Selecciona una dirección válida de la lista para origen y destino');
      return;
    }
    setPaso(paso + 1);
  };

  const pasoAnterior = () => setPaso(paso - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.aceptaLOPD) {
      setError('Debes aceptar la política de privacidad');
      return;
    }
    if (!form.nombre || !form.apellidos || !form.email || !form.telefono) {
      setError('Por favor completa todos los datos de contacto');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { aceptaLOPD, ...data } = form;
      await enviarPresupuesto(data, fotos);
      setMensaje(`¡Presupuesto enviado! Nos pondremos en contacto en breve.${fotos.length ? ` Hemos recibido ${fotos.length} foto${fotos.length > 1 ? 's' : ''}.` : ''}`);
      setForm({
        tipoMudanza: 'local', fechaMudanza: '', origen: '', destino: '', descripcion: '',
        nombre: '', apellidos: '', email: '', telefono: '', aceptaLOPD: false,
      });
      setSelectedAddress({ origen: null, destino: null });
      setFotos([]);
      setPaso(1);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al enviar el presupuesto');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background: '#FAF7F2', minHeight: '100vh' }}>
      <section className={styles.hero}>
        <div className={styles.heroContent}>
          <span className={styles.eyebrow} style={{ color: '#FFB380' }}>Presupuesto gratis</span>
          <h1>Cuéntanos tu mudanza</h1>
          <p>En menos de 2 minutos. Sin compromiso. Respuesta en 24h.</p>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.container}>
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.stepIndicator}>
              {[1, 2, 3, 4].map(p => (
                <div key={p} className={`${styles.step} ${paso >= p ? styles.active : ''} ${paso > p ? styles.completed : ''}`}>
                  <div className={styles.stepNumber}>{p}</div>
                  <span className={styles.stepLabel}>
                    {p === 1 ? 'Tipo' : p === 2 ? 'Ubicación' : p === 3 ? 'Detalles' : 'Contacto'}
                  </span>
                </div>
              ))}
            </div>

            {mensaje && <div className={styles.successMessage}>{mensaje}</div>}
            {error && <div className={styles.errorMessage}>{error}</div>}

            {paso === 1 && (
              <>
                <h2 style={{ marginBottom: '1.5rem', fontSize: '1.5rem' }}>¿Qué tipo de mudanza necesitas?</h2>
                <div className={styles.formGroup}>
                  <label>Tipo de mudanza</label>
                  <select name="tipoMudanza" value={form.tipoMudanza} onChange={handleChange}>
                    <option value="local">Mudanza local (Móstoles/Madrid)</option>
                    <option value="nacional">Mudanza nacional</option>
                    <option value="internacional">Mudanza internacional</option>
                    <option value="empresas">Mudanza de empresa</option>
                    <option value="guardamuebles">Guardamuebles</option>
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label>Fecha aproximada</label>
                  <input type="date" name="fechaMudanza" value={form.fechaMudanza} onChange={handleChange} />
                </div>
              </>
            )}

            {paso === 2 && (
              <>
                <h2 style={{ marginBottom: '1.5rem', fontSize: '1.5rem' }}>Origen y destino</h2>
                <AddressAutocomplete
                  label="¿Desde dónde? Dirección completa"
                  name="origen"
                  value={form.origen}
                  selected={selectedAddress.origen}
                  onValueChange={handleAddressValue}
                  onSelect={handleAddressSelect}
                  placeholder="Ej: Calle Río Duero 12, Móstoles"
                />
                <AddressAutocomplete
                  label="¿Hacia dónde? Dirección completa"
                  name="destino"
                  value={form.destino}
                  selected={selectedAddress.destino}
                  onValueChange={handleAddressValue}
                  onSelect={handleAddressSelect}
                  placeholder="Ej: Calle Alcalá 120, Madrid"
                />
              </>
            )}

            {paso === 3 && (
              <>
                <h2 style={{ marginBottom: '1.5rem', fontSize: '1.5rem' }}>Descripción y fotos</h2>
                <div className={styles.formGroup}>
                  <label>Cuéntanos un poco más (opcional)</label>
                  <textarea
                    name="descripcion"
                    value={form.descripcion}
                    onChange={handleChange}
                    placeholder="Ej: Piso de 3 habitaciones con trastero. Aproximadamente 20 cajas y muebles grandes."
                    style={{ minHeight: '120px' }}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <FaCamera style={{ color: '#D97441' }} />
                    Fotos (opcional)
                    <span style={{ fontWeight: 400, color: '#8B8B8B', fontSize: '0.85rem', marginLeft: '0.5rem' }}>
                      nos ayuda a darte un presupuesto más ajustado
                    </span>
                  </label>

                  <label
                    htmlFor="fotos-input"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      border: '2px dashed #E8E3DB',
                      borderRadius: '0.75rem',
                      padding: '2rem 1rem',
                      cursor: 'pointer',
                      background: '#FAF7F2',
                      transition: 'all 0.2s ease',
                      color: '#636E72'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#D97441';
                      e.currentTarget.style.background = '#FFF8F3';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#E8E3DB';
                      e.currentTarget.style.background = '#FAF7F2';
                    }}
                  >
                    <FaCloudUploadAlt style={{ fontSize: '2rem', color: '#D97441' }} />
                    <strong style={{ color: '#0F4C75' }}>
                      {fotos.length === 0 ? 'Haz clic para añadir fotos' : 'Añadir más fotos'}
                    </strong>
                    <span style={{ fontSize: '0.85rem' }}>
                      JPG, PNG, WEBP o HEIC · Máx {MAX_SIZE_MB}MB cada una · Hasta {MAX_FOTOS} fotos
                    </span>
                    <input
                      id="fotos-input"
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleFotos}
                      style={{ display: 'none' }}
                    />
                  </label>

                  {fotos.length > 0 && (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                      gap: '0.75rem',
                      marginTop: '1rem'
                    }}>
                      {fotos.map((file, i) => (
                        <div key={i} style={{ position: 'relative', aspectRatio: '1', borderRadius: '0.5rem', overflow: 'hidden', border: '1px solid #E8E3DB' }}>
                          <img
                            src={URL.createObjectURL(file)}
                            alt={file.name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onLoad={(e) => URL.revokeObjectURL(e.target.src)}
                          />
                          <button
                            type="button"
                            onClick={() => quitarFoto(i)}
                            style={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              background: 'rgba(0,0,0,0.6)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '50%',
                              width: 24,
                              height: 24,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              fontSize: '0.7rem'
                            }}
                            aria-label={`Quitar ${file.name}`}
                          >
                            <FaTimes />
                          </button>
                          <div style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                            color: 'white',
                            fontSize: '0.7rem',
                            padding: '0.75rem 0.4rem 0.3rem',
                            textAlign: 'center',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {(file.size / 1024 / 1024).toFixed(1)}MB
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {paso === 4 && (
              <>
                <h2 style={{ marginBottom: '1.5rem', fontSize: '1.5rem' }}>Tus datos de contacto</h2>
                <div className={styles.twoCol}>
                  <div className={styles.formGroup}>
                    <label>Nombre *</label>
                    <input type="text" name="nombre" value={form.nombre} onChange={handleChange} required />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Apellidos *</label>
                    <input type="text" name="apellidos" value={form.apellidos} onChange={handleChange} required />
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label>Email *</label>
                  <input type="email" name="email" value={form.email} onChange={handleChange} required />
                </div>
                <div className={styles.formGroup}>
                  <label>Teléfono *</label>
                  <input type="tel" name="telefono" value={form.telefono} onChange={handleChange} required />
                </div>
                <div className={styles.formCheckbox}>
                  <input
                    type="checkbox"
                    id="lopd"
                    name="aceptaLOPD"
                    checked={form.aceptaLOPD}
                    onChange={handleChange}
                    required
                  />
                  <label htmlFor="lopd">
                    Acepto la política de privacidad y el tratamiento de mis datos personales conforme al RGPD
                  </label>
                </div>
                {fotos.length > 0 && (
                  <div style={{
                    background: '#FFF8F3',
                    border: '1px solid #F5DFC9',
                    borderRadius: '0.5rem',
                    padding: '0.75rem 1rem',
                    fontSize: '0.9rem',
                    color: '#8B4513',
                    marginBottom: '1.5rem'
                  }}>
                    📎 <strong>{fotos.length}</strong> foto{fotos.length > 1 ? 's' : ''} adjunta{fotos.length > 1 ? 's' : ''} a la solicitud
                  </div>
                )}
              </>
            )}

            <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
              {paso > 1 && (
                <button
                  type="button"
                  onClick={pasoAnterior}
                  style={{
                    flex: 1,
                    background: 'transparent',
                    color: '#0F4C75',
                    border: '2px solid #E8E3DB',
                    padding: '1rem',
                    borderRadius: '0.6rem',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  ← Anterior
                </button>
              )}
              {paso < 4 ? (
                <button type="button" onClick={siguientePaso} style={{ flex: 1 }} className={styles.formButton}>
                  Siguiente →
                </button>
              ) : (
                <button type="submit" disabled={loading} style={{ flex: 1 }} className={styles.formButton}>
                  {loading ? 'Enviando...' : 'Enviar presupuesto'}
                </button>
              )}
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
